import {
  type DatabaseBlockDataSource,
  databaseViewInitTemplate,
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
  provisionShardedDatabaseBlockDataSource,
} from '@blocksuite/affine-block-database';
import {
  cleanupIcon,
  DEFAULT_AUTOMATION_HEIGHT,
  DEFAULT_AUTOMATION_WIDTH,
  DEFAULT_CLEANUP_SIZE,
  DEFAULT_CLEANUP_TIMEZONE,
  DEFAULT_ELSTER_HEIGHT,
  DEFAULT_ELSTER_WIDTH,
} from '@blocksuite/affine-block-streamlain';
import { EdgelessCRUDIdentifier } from '@blocksuite/affine-block-surface';
import {
  type ElementCreationSource,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import { type Bound, serializeXYWH } from '@blocksuite/global/gfx';
import { PlayIcon } from '@blocksuite/icons/lit';
import type { BlockComponent } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { PublicationError } from '@streamlain/doc-publication-contract';
import { html, type TemplateResult } from 'lit';
import * as Y from 'yjs';

export type StreamLainDraggableTool = {
  name: 'database' | 'automation' | 'cleanup';
  icon: TemplateResult;
  standardWidth?: number;
  render: (
    bound: Bound,
    edgeless: BlockComponent,
    control?: ElementCreationSource
  ) => Promise<string | null>;
};

// Database Icon for Canvas
const databaseIconTemplate = (size: number) =>
  html`<svg
    width=${size}
    height=${size}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      x="3.85"
      y="3.35"
      width="12.3"
      height="13.3"
      rx="1.7"
      stroke="currentColor"
      stroke-width="1.3"
    ></rect>
    <path
      d="M4.2 7H15.8M7.35 7V16.35"
      stroke="currentColor"
      stroke-width="1.3"
      stroke-linecap="round"
    ></path>
    <path
      d="M9.85 10.15H14.35M9.85 13H14.35"
      stroke="currentColor"
      stroke-width="1.3"
      stroke-linecap="round"
    ></path>
  </svg>`;

export const databaseIcon = databaseIconTemplate(40);

export const databaseDenseIcon = databaseIconTemplate(20);

// Automation Icon: same glyph family as the workspace Automation sidebar tab.
const automationDatabaseIcon = (size: number, databaseSize: number) => html`
  <span
    style=${`position:relative;display:inline-flex;width:${size}px;height:${size}px;align-items:center;justify-content:center`}
  >
    ${PlayIcon({ width: `${size}px`, height: `${size}px` })}
    <span
      style=${`position:absolute;right:-1px;bottom:-1px;display:flex;width:${databaseSize}px;height:${databaseSize}px;align-items:center;justify-content:center;border-radius:2px;background:var(--streamlain-v2-layer-background-primary, #fff)`}
    >
      ${databaseIconTemplate(databaseSize)}
    </span>
  </span>
`;

export const automationDenseIcon = automationDatabaseIcon(20, 9);

export const cleanupDenseIcon = cleanupIcon();

export const cleanupRender: StreamLainDraggableTool['render'] = async (
  bound,
  edgeless,
  control = 'toolbar:dnd'
) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const id = edgeless.std.get(EdgelessCRUDIdentifier).addBlock(
    'affine:automation',
    {
      executionKind: 'cleanup',
      xywh: serializeXYWH(
        bound.x,
        bound.y,
        DEFAULT_CLEANUP_SIZE,
        DEFAULT_CLEANUP_SIZE
      ),
      triggerConfig: {
        type: 'scheduled',
        enabled: false,
        cronPreset: 'nightly',
        timezone: DEFAULT_CLEANUP_TIMEZONE,
      },
    },
    gfx.doc.root?.id
  );
  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control,
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'cleanup',
  });
  return id ?? null;
};

// Automation render function - creates an Automation Block on Canvas
export const automationRender: StreamLainDraggableTool['render'] = async (
  bound,
  edgeless,
  control = 'toolbar:dnd'
) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const crud = edgeless.std.get(EdgelessCRUDIdentifier);

  const x = bound.x;
  const y = bound.y;

  const automationId = crud.addBlock(
    'affine:automation',
    {
      xywh: serializeXYWH(
        x,
        y,
        DEFAULT_AUTOMATION_WIDTH,
        DEFAULT_AUTOMATION_HEIGHT
      ),
    },
    gfx.doc.root?.id
  );

  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control,
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'automation',
  });

  return automationId ?? null;
};

const pendingCanvasDatabaseCreations = new WeakSet<object>();
export const databaseRender: StreamLainDraggableTool['render'] = async (
  bound,
  edgeless,
  control
) => {
  const owner = edgeless.std.get(GfxControllerIdentifier).doc;
  if (pendingCanvasDatabaseCreations.has(owner)) return null;
  pendingCanvasDatabaseCreations.add(owner);
  try {
    return await renderDatabase(bound, edgeless, control);
  } finally {
    pendingCanvasDatabaseCreations.delete(owner);
  }
};

// Database render function - creates a Database on Canvas
const renderDatabase: StreamLainDraggableTool['render'] = async (
  bound,
  edgeless,
  control = 'toolbar:dnd'
) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const crud = edgeless.std.get(EdgelessCRUDIdentifier);

  // Use model coordinates from bound (already converted by drag controller)
  const x = bound.x;
  const y = bound.y;

  // Create database block under root (same pattern as Note)
  // Schema: parent: ['affine:note', '@root']
  const provisioned = await provisionShardedDatabaseBlockDataSource(gfx.doc, {
    props: {
      xywh: serializeXYWH(
        x,
        y,
        DEFAULT_CANVAS_DATABASE_WIDTH,
        DEFAULT_CANVAS_DATABASE_HEIGHT
      ),
    },
    addBlock: (flavour, props, parentId) =>
      crud.addBlock(flavour, props, parentId),
    initialEmptyRow: true,
    initDataSource: datasource => databaseViewInitTemplate(datasource, 'table'),
  });
  if (provisioned.status === 'discarded')
    throw new PublicationError('CANCELLED');
  const databaseId = provisioned.databaseBlockId;

  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control,
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'database',
  });

  return databaseId;
};

// =============================================================================
// ELSTER
// =============================================================================

// ELSTER Icon — government building silhouette
export const elsterIcon = html`<svg
  width="40"
  height="40"
  viewBox="0 0 24 24"
  fill="currentColor"
>
  <path
    d="M12 2L2 8v2h20V8L12 2zm0 2.5L18 8H6l6-3.5zM4 12v6h3v-6H4zm5 0v6h3v-6H9zm5 0v6h3v-6h-3zm5 0v6h1V12h-1zM3 12v6H2v2h20v-2h-1v-6h-1v6h-2v-6h-1v6h-2v-6h-1v6h-2v-6H9v6H7v-6H4v6H3v-6z"
  />
</svg>`;

// ELSTER + DB Icon — government building with plus badge
export const elsterWithDbIcon = html`<svg
  width="40"
  height="40"
  viewBox="0 0 24 24"
  fill="currentColor"
>
  <path
    d="M12 2L2 8v2h20V8L12 2zm0 2.5L18 8H6l6-3.5zM4 12v6h3v-6H4zm5 0v6h3v-6H9zm5 0v6h3v-6h-3zM2 20v2h20v-2H2z"
  />
  <circle
    cx="19"
    cy="5"
    r="4.5"
    fill="var(--streamlain-v2-brand-primary, #1E96EB)"
  />
  <path
    d="M19 3v4M17 5h4"
    stroke="white"
    stroke-width="1.5"
    stroke-linecap="round"
    fill="none"
  />
</svg>`;

// ELSTER render — creates standalone ELSTER block
export const elsterRender: StreamLainDraggableTool['render'] = async (
  bound,
  edgeless
) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const crud = edgeless.std.get(EdgelessCRUDIdentifier);

  const elsterId = crud.addBlock(
    'affine:elster',
    {
      xywh: serializeXYWH(
        bound.x,
        bound.y,
        DEFAULT_ELSTER_WIDTH,
        DEFAULT_ELSTER_HEIGHT
      ),
    },
    gfx.doc.root?.id
  );

  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'toolbar:dnd',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'elster',
  });

  return elsterId ?? null;
};

// =============================================================================
// UStVA Kennzahlen Column Definitions
// =============================================================================

/** Column definition for UStVA database template */
interface KzColumn {
  name: string;
  type: 'number';
}

/**
 * All 25 UStVA Kennzahlen as database columns.
 * Column naming convention: "Sprechender Name (KzXX)"
 * The ELSTER node extracts the Kz code via regex: /\(Kz(\d+)\)/
 */
const USTVA_COLUMNS: KzColumn[] = [
  // Umsätze steuerpflichtig
  { name: 'Umsätze 19% (Kz81)', type: 'number' },
  { name: 'Umsätze 7% (Kz86)', type: 'number' },
  { name: 'Umsätze andere Sätze (Kz35)', type: 'number' },
  { name: 'Steuer andere Sätze (Kz36)', type: 'number' },
  // Steuerfrei mit Vorsteuer
  { name: 'EU-Lieferungen §4/1b (Kz77)', type: 'number' },
  { name: 'Ausfuhrlieferungen §4/1a (Kz76)', type: 'number' },
  // Steuerfrei ohne Vorsteuer
  { name: 'Steuerfrei ohne Vorsteuer (Kz48)', type: 'number' },
  // Innergemeinschaftliche Erwerbe
  { name: 'IGE allgemein (Kz41)', type: 'number' },
  { name: 'IGE Steuer 19% (Kz43)', type: 'number' },
  { name: 'IGE Steuer 7% (Kz44)', type: 'number' },
  { name: 'IGE Fahrzeuge (Kz46)', type: 'number' },
  { name: 'IGE Fahrzeuge Steuer (Kz47)', type: 'number' },
  // §13b Reverse Charge
  { name: '§13b Leistungen (Kz89)', type: 'number' },
  { name: '§13b Steuer (Kz93)', type: 'number' },
  // Sonstiges
  { name: 'Nicht steuerbar (Kz42)', type: 'number' },
  { name: 'Land-/Forstwirtschaft (Kz60)', type: 'number' },
  { name: 'Steuer Land-/Forstw. (Kz21)', type: 'number' },
  // Vorsteuer
  { name: 'Vorsteuer Rechnungen (Kz66)', type: 'number' },
  { name: 'Vorsteuer IGE (Kz61)', type: 'number' },
  { name: 'Einfuhrumsatzsteuer (Kz62)', type: 'number' },
  { name: 'Vorsteuer §13b (Kz67)', type: 'number' },
  { name: 'Korrektur §15a Erhöhung (Kz63)', type: 'number' },
  { name: 'Korrektur §15a Minderung (Kz64)', type: 'number' },
  { name: 'Pauschalierung §23 (Kz59)', type: 'number' },
  // Sondervorauszahlung
  { name: 'Sondervorauszahlung 1/11 (Kz39)', type: 'number' },
];

// =============================================================================
// ELSTER + Datenbanken render
// =============================================================================

/** Canvas dimensions for ELSTER + DB layout */
const USTVA_DB_WIDTH = 800;
const USTVA_DB_HEIGHT = 300;

/**
 * Creates a database block with columns and optional initial data.
 * Returns [blockId, dataSource] for further manipulation.
 */
async function createDatabaseWithColumns(
  edgeless: BlockComponent,
  title: string,
  x: number,
  y: number,
  width: number,
  height: number,
  columns: { name: string; type: string }[]
): Promise<{ id: string; datasource: DatabaseBlockDataSource } | null> {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const crud = edgeless.std.get(EdgelessCRUDIdentifier);

  const provisioned = await provisionShardedDatabaseBlockDataSource(gfx.doc, {
    title,
    props: {
      xywh: serializeXYWH(x, y, width, height),
    },
    addBlock: (flavour, props, parentId) =>
      crud.addBlock(flavour, props, parentId),
    initDataSource: datasource => {
      for (const col of columns)
        datasource.propertyAdd('end', { type: col.type, name: col.name });
      datasource.viewManager.viewAdd('table');
    },
  });
  if (provisioned.status === 'discarded')
    throw new PublicationError('CANCELLED');
  const datasource = provisioned.dataSource;

  return { id: provisioned.databaseBlockId, datasource };
}

/**
 * ELSTER + Datenbanken render function.
 *
 * Creates:
 * 1. "UStVA Daten" database (left, 800×300) with 25 Kz columns + 1 example row
 * 2. "ELSTER" block (center, 640×200)
 * 3. (Removed — history is now in Prisma ElsterFiling table)
 * 4. Sets edge props: inputTableId
 */
const renderElsterWithDatabases: StreamLainDraggableTool['render'] = async (
  bound,
  edgeless
) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const crud = edgeless.std.get(EdgelessCRUDIdentifier);

  // Center position = ELSTER block position
  const cx = bound.x;
  const cy = bound.y;

  // 1. Create UStVA Daten database (left of ELSTER)
  const ustvaX = cx - DEFAULT_ELSTER_WIDTH / 2 - 60 - USTVA_DB_WIDTH;
  const ustvaY = cy + DEFAULT_ELSTER_HEIGHT / 2 - USTVA_DB_HEIGHT / 2;
  const ustvaResult = await createDatabaseWithColumns(
    edgeless,
    'UStVA Daten',
    ustvaX,
    ustvaY,
    USTVA_DB_WIDTH,
    USTVA_DB_HEIGHT,
    USTVA_COLUMNS
  );

  // Add example row to UStVA Daten
  if (ustvaResult) {
    const { datasource } = ustvaResult;
    const rowId = datasource.rowAdd('end');
    // Set title cell (Zeitraum)
    const titlePropId = 'title';
    datasource.cellValueChange(rowId, titlePropId, new Y.Text('Januar 2026'));
    // Set example values for key Kennzahlen
    const props = datasource.getBoundedPropertyIdsWindow().propertyIds;
    for (const propId of props) {
      const name = datasource.propertyNameGet(propId);
      if (name === 'Umsätze 19% (Kz81)') {
        datasource.cellValueChange(rowId, propId, 50000);
      } else if (name === 'Umsätze 7% (Kz86)') {
        datasource.cellValueChange(rowId, propId, 5000);
      } else if (name === 'Vorsteuer Rechnungen (Kz66)') {
        datasource.cellValueChange(rowId, propId, 8500);
      }
    }
  }

  // 2. Create ELSTER block (center)
  const elsterId = crud.addBlock(
    'affine:elster',
    {
      xywh: serializeXYWH(cx, cy, DEFAULT_ELSTER_WIDTH, DEFAULT_ELSTER_HEIGHT),
      inputTableId: ustvaResult?.id ?? null,
      inputTableName: ustvaResult ? 'UStVA Daten' : null,
    },
    gfx.doc.root?.id
  );

  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'toolbar:dnd',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'elster-with-databases',
  });

  return elsterId ?? null;
};

export const elsterWithDatabasesRender: StreamLainDraggableTool['render'] =
  async (bound, edgeless) => {
    const owner = edgeless.std.get(GfxControllerIdentifier).doc;
    if (pendingCanvasDatabaseCreations.has(owner)) return null;
    pendingCanvasDatabaseCreations.add(owner);
    try {
      return await renderElsterWithDatabases(bound, edgeless);
    } finally {
      pendingCanvasDatabaseCreations.delete(owner);
    }
  };
